import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaEye, FaFileInvoiceDollar } from "react-icons/fa";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import Grid from '../components/Grid';
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa"; 
import ExcelJS from "exceljs";


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function DispatchedStockOnLoan({ onAddDispatchedStockOnLoanClick }) {
  const [dispatchedStockOnLoan, setDispatchedStockOnLoan] = useState([]);
  const [selectedDispatchedStockOnLoan, setSelectedDispatchedStockOnLoan] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [userRole, setUserRole] = useState("");
  const [selectedSearchField, setSelectedSearchField] = useState("dispatchedStockOnLoanCode");
  const [isViewingInvoice, setIsViewingInvoice] = useState(false);
  const [grnInvoiceDetails, setGrnInvoiceDetails] = useState([]);
  const [invoicePermissions, setInvoicePermissions] = useState({});

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
  
        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        setUserRole(userResponse.data.role); // Set the user role
      } catch (error) {
        console.error("Error fetching user role:", error);
        toast.error("Error fetching user role.");
      }
    };
  
    fetchUserRole();
  }, []);

  useEffect(() => {
    const fetchDispathedStockOnLoan = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/dispatchedStockOnLoan`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const dispatchedStockOnLoan = response.data.dispatchedStockOnLoan || [];

        const formattedDispatchedStockOnLoan = await Promise.all(
          dispatchedStockOnLoan.map(async (order) => {
            let status = order.status;

            try {
              const invRes = await axios.get(
                `${API_BASE_URL}/grnInvoices/by-purchase-order/${order.dispatched_stock_on_loan_code}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );

              if (invRes.data.invoices && invRes.data.invoices.length > 0) {
                status = "Invoice Created";
              }
            } catch (err) {
              console.warn(`No invoices found for ${order.dispatched_stock_on_loan_code}`);
            }

            if (
              status !== "Invoice Created" &&
              (order.status === "Fully Received" || order.status === "Partially Received")
            ) {
              status = "Delivered";
            }

            let allowed = false;
            try {
              if (status === "Invoice Created") {
                const permRes = await axios.get(
                  `${API_BASE_URL}/clusters/invoice-permission/${order._id}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                allowed = permRes.data.allowed;
              }
            } catch (err) {
              console.error("Permission check failed:", err);
              allowed = false;
            }

            return {
              id: order._id,
              dispatchedStockOnLoanCode: order.dispatched_stock_on_loan_code || "N/A",
              cluster_id: order.cluster_id?._id || null,
              fromLocation: order.from_location_id?.location_name || "Unknown",
              toOrganization: order.to_organization_id?.organization_name || "Unknown",
              toLocation: order.to_location_id?.location_name || "Unknown",
              createdDate: new Date(order.created_date).toLocaleDateString("en-GB"),
              created_by: order.created_by?.username || "Unknown",
              status,
              createdAt: order.createdAt,
              originalOrder: order,
              allowed, // store permission here
            };
          })
        );

        const permissions = {};
        formattedDispatchedStockOnLoan.forEach(order => {
          permissions[order.id] = order.allowed;
        });

        setInvoicePermissions(permissions);
        setDispatchedStockOnLoan(formattedDispatchedStockOnLoan);

      } catch (error) {
        console.error("Error fetching dispatched stock on loan:", error);
        toast.error("Error fetching dispatched stock on loan.");
      }
    };

    console.log("Fetching dispatched stock on loan: ", dispatchedStockOnLoan);

    fetchDispathedStockOnLoan();
  }, []);

  const handleDispatchedStockOnLoanClick = async (dispatchedStockOnLoan) => {

    console.log("Dispatched Stock On Loan Clicked: ", dispatchedStockOnLoan);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }
  
      const response = await axios.get(
        `${API_BASE_URL}/dispatchedStockOnLoan/${dispatchedStockOnLoan.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const detailedOrder = response.data.dispatchedStockOnLoan;
      console.log("Detailed Dispatched Stock On Loan:", detailedOrder);
  
      const formattedProducts = detailedOrder.dispatchedStockItems.map((item) => ({
        id: item.product_id._id,
        product_name: item.product_id.product_name,
        product_image: item.product_id.product_image,
        product_code: item.product_id.product_code,
        quantity: item.qty,
        unit: item.product_id.unit?.unit_name,
      }));

      console.log("Selected Dispatched Stock On Loan:", formattedProducts);
  
      setSelectedDispatchedStockOnLoan({
        ...detailedOrder,
        products: formattedProducts,
        sourceLocation: detailedOrder.from_location_id.location_name,
        destinationOrganization: detailedOrder.to_organization_id.organization_name,
        destinationLocation: detailedOrder.to_location_id.location_name,
        selectedProducts: formattedProducts || [],
      });
      
    } catch (error) {
      console.error("Error fetching dispatched stock on loan details:", error.message);
      toast.error("Error fetching dispatched stock on loan details.");
    }
  };

  const handleClosePopup = () => {
    setSelectedDispatchedStockOnLoan(null);
  };

  const sortedDispatchedStockOnLoan = [...dispatchedStockOnLoan].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const filteredDispatchedStockOnLoan = sortedDispatchedStockOnLoan.filter((order) => {
    const fieldValue = order[selectedSearchField]
      ? order[selectedSearchField].toString().toLowerCase()
      : "";

    return (
      fieldValue.includes(searchTerm.toLowerCase()) &&
      order.status !== "Partially Received" &&
      order.status !== "Fully Received"
    );
  });

  const handleInvoiceIconClick = async (dispatchedStock) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token is missing. Please log in again.");
      return;
    }

    const purchaseOrderCode = dispatchedStock.originalOrder.dispatched_stock_on_loan_code;

    const res = await axios.get(
      `${API_BASE_URL}/grnInvoices/by-purchase-order/${purchaseOrderCode}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setGrnInvoiceDetails(res.data.invoices || []);
    setIsViewingInvoice(true);
  } catch (error) {
    console.error("Error fetching invoices for purchase order:", error);
    toast.error(
      error.response?.data?.message || "Error fetching invoices for purchase order."
    );
  }
};


  const handleDownloadExcel = async () => {
    if (filteredDispatchedStockOnLoan.length === 0) {
      toast.warn("No stock data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Dispatched Stock On Loan");

    // Header row
    const headerRow = [
      "Dispatched Stock On Loan Code",
      "Source Location",
      "Source Organization",
      "Destination Location",
      "Date",
      "Created By",
      "Status",
    ];
    worksheet.addRow(headerRow);

    // Header styling
    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" },
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Add filtered rows
    filteredDispatchedStockOnLoan.forEach((stock) => {
      worksheet.addRow([
        stock.dispatchedStockOnLoanCode || "",
        stock.fromLocation || "",
        stock.toOrganization || "",
        stock.toLocation || "",
        stock.createdDate || "",
        stock.created_by || "",
        stock.status || "",
      ]);
    });

    // Auto-adjust column width
    worksheet.columns.forEach((column, i) => {
      let maxLength = headerRow[i].length;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellLength = cell.value ? cell.value.toString().length : 0;
        if (cellLength > maxLength) maxLength = cellLength;
      });
      column.width = maxLength + 2;
    });

    // Export as Excel
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "dispatched_stock_on_loan.xlsx");
  };

  const columns = [
    { field: "dispatchedStockOnLoanCode", headerName: "Dispatched Stock On Loan Code", flex: 1 },
    { field: "createdDate", headerName: "Created Date", flex: 1 },
    { field: "fromLocation", headerName: "Source Location", flex: 1 },
    { field: "toOrganization", headerName: "Destination Organization", flex: 1 },
    { field: "toLocation", headerName: "Destination Location", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const status = params.row.status;
    
        // Map statuses to classes
        const statusClassMap = {
          "TO Created": "bg-blue-500 text-white",
          "Dispatched": "bg-green-500 text-white",
          "Drafted": "bg-lime-600 text-white",
          "Invoice Created": "bg-purple-900 text-white",
          "Delivered": "bg-teal-600 text-white",
        };
    
        // Get the class based on status or default to a neutral color
        const statusClass = statusClassMap[status] || "bg-gray-500 text-white"; // Default class if status not found
    
        return (
          <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    { field: "created_by", headerName: "Created By", flex: 1 },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <div className="flex gap-2">
          {/* View Button */}
          <button
            onClick={() => handleDispatchedStockOnLoanClick(params.row)}
            className="bg-yellow-500 text-white p-2 flex items-center justify-center rounded"
            style={{ width: "30px", height: "30px" }}
          >
            <FaEye className="text-white" />
          </button>

          <button
            onClick={() => handleInvoiceIconClick(params.row)}
            className={`p-2 flex items-center justify-center rounded ${
              invoicePermissions[params.row.id]
                ? "bg-[#4CAF50] text-white cursor-pointer"
                : "bg-gray-400 text-gray-200 cursor-not-allowed"
            }`}
            style={{ width: "30px", height: "30px" }}
            disabled={!invoicePermissions[params.row.id]}
          >
            <FaFileInvoiceDollar />
          </button>
        </div>
      ),
    }
  ];

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-semibold">Outgoing Stock on Loan</h1>
          {(userRole === "Company Admin" || userRole === "Farm User") && (
            <button
              onClick={onAddDispatchedStockOnLoanClick}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg"
            >
              Add Outgoing Stock on Loan
            </button>
          )}
        </div>
        <div className="bg-white p-4 rounded-lg shadow mb-8">
          <div className="flex justify-end mb-4">
          </div>
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="incomingStockOnLoanCode">Dispatched Code</option>
                <option value="fromLocation">Source Location</option>
                <option value="fromOrganization">Source Organization</option>
                <option value="incomingLocation">Destination Location</option>
                <option value="createdDate">Date</option>
                <option value="created_by">Created By</option>
                <option value="status">Status</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Products"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>   
          <Grid rows={filteredDispatchedStockOnLoan} columns={columns} getRowId={(row) => row.id}/>
        </div>
        {selectedDispatchedStockOnLoan && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto">
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <h2 className="text-xl font-semibold mb-4">
                {selectedDispatchedStockOnLoan.dispatched_stock_on_loan_code}
              </h2>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Source Location</label>
                <input
                  type="text"
                  value={selectedDispatchedStockOnLoan.from_location_id?.location_name || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Destination Organization</label>
                <input
                  type="text"
                  value={selectedDispatchedStockOnLoan.to_organization_id?.organization_name || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Destination Location</label>
                <input
                  type="text"
                  value={selectedDispatchedStockOnLoan.to_location_id?.location_name || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Created Date</label>
                <input
                  type="text"
                  value={selectedDispatchedStockOnLoan.created_date || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Created By</label>
                <input
                  type="text"
                  value={selectedDispatchedStockOnLoan.created_by?.username || "Unknown"}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Status</label> 
                <input
                  type="text"
                  value={selectedDispatchedStockOnLoan.status || "Unknown"}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">
                   Selected Products
                </h3>
                <div style={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={selectedDispatchedStockOnLoan?.products || []} // Use the products from selectedProductionBatch
                    columns={[
                      { field: "product_code", headerName: "ID", width: 100 },
                      {
                        field: "product_image",
                        headerName: "Image",
                        width: 150,
                        renderCell: (params) =>
                        params.row.product_image ? (
                          <img
                            src={`${process.env.REACT_APP_IMAGE_BASE_URL}${params.row.product_image}`}
                            alt="Product"
                            className="h-16 w-16 object-cover rounded-md"
                          />
                          ) : (
                            "No Image"
                          ),
                      },
                      {
                        field: "product_name",
                        headerName: "Product Name",
                        width: 150,
                      },
                      {
                        field: "quantity",
                        headerName: "Quantity",
                        width: 150,
                        headerAlign: "right",
                        align: "right"
                      },
                      { 
                          field: "unit", 
                          headerName: "Units", 
                          width: 150,                    
                          headerAlign: "center", 
                          align: "center" 
                        },
                    ]}
                    pageSize={5}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {isViewingInvoice && grnInvoiceDetails.length > 0 && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 max-h-[90vh] overflow-y-auto relative">
              <button
                onClick={() => setIsViewingInvoice(false)}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <h2 className="text-xl font-semibold mb-4 text-center">Created Invoices</h2>
              {grnInvoiceDetails.map((invoice, idx) => (
                <div key={invoice._id} className="mb-6 border-t pt-4">
                  <h3 className="font-semibold text-lg mb-2">
                    Invoice #{idx + 1} - <span className="text-blue-600">{invoice.invoice_code}</span>
                  </h3>

                  <DataGrid
                    autoHeight
                    rows={invoice.product_items.map((item, index) => ({
                      id: index,
                      ...item,
                      unit_name: item.unit?.unit_name || "N/A",
                    }))}
                    columns={[
                      { field: "product_name", headerName: "Product", flex: 2 },
                      { field: "quantity", headerName: "Qty", flex: 1 },
                      { field: "unit_name", headerName: "Unit", flex: 1 }, 
                      {
                        field: "unit_price",
                        headerName: "Unit Price (Rs.)",
                        flex: 1,
                        renderCell: (params) =>
                          `Rs. ${Number(params.row.unit_price).toFixed(2)}`,
                      },
                      {
                        field: "total",
                        headerName: "Total (Rs.)",
                        flex: 1,
                        renderCell: (params) =>
                          `Rs. ${(params.row.unit_price * params.row.quantity).toFixed(2)}`,
                      },
                    ]}
                    pageSize={5}
                    rowsPerPageOptions={[5]}
                    disableSelectionOnClick
                    hideFooterSelectedRowCount
                  />

                  <div className="mt-2 text-right text-sm">
                    <div><strong>Total:</strong> Rs. {Number(invoice.amount).toFixed(2)}</div>
                    <div><strong>Balance:</strong> Rs. {Number(invoice.balance).toFixed(2)}</div>
                    <div><strong>Due Date:</strong> {new Date(invoice.due_date).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}        
      </div>
    </div>
  );
}

export default DispatchedStockOnLoan;
